import React, { useEffect, useMemo, useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import RichContent from '../../components/public/RichContent';
import { formatMarathiNumber, toMarathiDigits } from '../../utils/marathi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './AboutPage.css';

export default function AboutPage() {
  const { tenantData, loading } = useTenant();
  const [expanded, setExpanded] = useState({});

  const sections = useMemo(() => {
    const tenantSections = tenantData?.tenant?.about_sections;
    if (Array.isArray(tenantSections) && tenantSections.length) {
      return [...tenantSections].sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    const aboutPage = tenantData?.pages?.find(p => p.slug === 'about');
    const fallbackContent = tenantData?.tenant?.about_text || aboutPage?.content;
    return fallbackContent ? [{ id: 'about', heading: 'गावाची सविस्तर ओळख', content: fallbackContent, order: 0 }] : [];
  }, [tenantData]);

  useEffect(() => {
    if (!window.location.hash) return;
    const targetId = window.location.hash.replace('#about-section-', '');
    if (!targetId) return;
    setExpanded(prev => ({ ...prev, [targetId]: true }));
    setTimeout(() => document.getElementById(`about-section-${targetId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  }, [sections]);

  if (loading || !tenantData) return <LoadingSpinner fullPage message="गावाची माहिती लोड होत आहे..." />;

  const { tenant, gallery = [] } = tenantData;
  const galleryImages = gallery.filter(item => item.media_type !== 'video');
  const summaryImage = galleryImages[4]?.image_url || galleryImages[1]?.image_url || '/HeaderBgImages/1.png';
  const stats = [
    { icon: '⌖', label: 'स्थान', value: `${tenant?.taluka || 'दौंड'} तालुका, ${tenant?.district || 'पुणे'} जिल्हा` },
    { icon: '👥', label: 'लोकसंख्या', value: formatMarathiNumber(tenant?.population || 3051) },
    { icon: '⌂', label: 'एकूण कुटुंबे', value: formatMarathiNumber(tenant?.total_households || 583) },
    { icon: '▤', label: 'साक्षरता दर', value: toMarathiDigits(tenant?.literacy_rate || '78.42%') },
  ];
  const quickFacts = [
    ['पिन कोड', tenant?.pincode || '412203'],
    ['रस्ते लांबी', tenant?.road_length || '— कि.मी.'],
    ['पर्जन्यमान', tenant?.rainfall || '— मि.मी.'],
    ['मुख्य व्यवसाय', tenant?.main_occupation || 'शेती'],
    ['ग्रामदैवत', tenant?.village_deity || 'श्री भैरवनाथ महाराज'],
    ['ग्रामपंचायत स्थापना', tenant?.established_year || '1973'],
  ];

  const jumpTo = id => {
    setExpanded(prev => ({ ...prev, [id]: true }));
    setTimeout(() => document.getElementById(`about-section-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  return (
    <div className="about-page">
      <section className="about-hero about-hero--image">
        <div>
          <span>मुख्यपृष्ठ&nbsp; › &nbsp;आमच्याबद्दल</span>
          <h1>आमच्याबद्दल</h1>
          <p>{tenant?.name || 'ग्रामपंचायत'} यांची सविस्तर माहिती, दृष्टी, उद्दिष्टे, प्रशासकीय रचना आणि सदस्यांची माहिती.</p>
        </div>
      </section>

      <section className="about-overview-grid about-overview-grid--facts">
        <div className="about-summary-card" style={summaryImage ? { backgroundImage: `linear-gradient(110deg, rgba(3,35,79,.96), rgba(3,35,79,.62)), url("${summaryImage}")` } : undefined}>
          <div><span>गावाची माहिती</span><h2>{tenant?.name || 'ग्रामपंचायत'}</h2><p>{tenant?.taluka}, {tenant?.district}, {tenant?.state}</p></div>
          <div className="about-stat-row">
            {stats.map(s => <div key={s.label}><span>{s.icon}</span><small>{s.label}</small><strong>{s.value}</strong></div>)}
          </div>
        </div>
        <div className="about-facts-card">
          <h2>गावाची ठळक माहिती</h2>
          <div>{quickFacts.map(([label, value], i) => <p key={label}><span>{['●','▰','💧','♣','⌂','▥'][i]}</span><strong>{label}</strong><b>{toMarathiDigits(value)}</b></p>)}</div>
        </div>
      </section>

      {sections.length > 0 && (
        <nav className="dynamic-section-nav" aria-label="आमच्याबद्दल विभाग">
          {sections.map((section, index) => <button key={section.id || index} onClick={() => jumpTo(section.id || index)}>{section.heading || `विभाग ${index + 1}`}</button>)}
        </nav>
      )}

      <section className="dynamic-about-sections">
        {sections.map((section, index) => {
          const id = section.id || index;
          const isOpen = expanded[id] ?? index === 0;
          return (
            <article id={`about-section-${id}`} className="dynamic-about-card" key={id}>
              <button className="dynamic-about-heading" onClick={() => setExpanded(prev => ({ ...prev, [id]: !isOpen }))}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <strong>{section.heading || `विभाग ${index + 1}`}</strong>
                <b>{isOpen ? '−' : '+'}</b>
              </button>
              {isOpen && <RichContent html={section.content} className="dynamic-about-content" />}
            </article>
          );
        })}
      </section>

    </div>
  );
}
